import { animate as motionAnimate } from "motion/mini";



// The launch animation (D175, docs/design/motion.md "Splash"). Its first frame is already in the page (vite.config.ts writes it into index.html from the brand master), so the first paint matches
// the system splash before any script runs. This file, in the page's first small chunk, draws the thread and brings in the wordmark, then leaves once the app is ready. It plays only when the
// page loads, so never on a resume, and it lets taps through to the app under it.

/** The tokens of motion.md, in seconds. */
const FAST = 0.15;
const BASE = 0.22;
const SLOW = 0.32;
const THREAD = 0.7;
const EASE_STANDARD = [0.2, 0.8, 0.2, 1] as const;
const EASE_EXIT = [0.4, 0, 1, 1] as const;

/** When the woven thread comes in, for a slow load, in ms. */
const SLOW_AFTER = 1500;
/** The splash never outstays this, in ms, even if nothing says the app is ready: an app that failed should show its error. */
const HOLD_AT_MOST = 15_000;

/** What the splash needs of Motion: an animation that says when it has finished. */
export type TSplashAnimate = (
  target: Element,
  keyframes: Record<string, Array<string | number>>,
  options: { duration: number; delay?: number; ease?: readonly number[] },
) => PromiseLike<unknown>;

/** What the tests can replace. */
export interface TSplashOptions {
  root?: Pick<Document, "getElementById" | "querySelector">;
  animate?: TSplashAnimate;
  /** Whether the person asked for less motion; the system's answer by default. */
  reduced?: boolean;
}

/** Tells the running splash that the app is ready. */
let announce: (() => void) | null = null;

/**
 * Says the app is ready, so the splash can leave. Nothing happens if none is running.
 */
export function splashReady(): void {
  announce?.();
}

/**
 * Plays the splash already in the page, if there is one.
 *
 * @param options - What the tests replace.
 * @returns Resolves once it has left.
 */
export async function startSplash(options: TSplashOptions = {}): Promise<void> {
  const root = options.root ?? document;
  const splash = root.getElementById("splash");

  if (splash === null) {
    return;
  }

  const animate: TSplashAnimate = options.animate ?? (motionAnimate as unknown as TSplashAnimate);
  const reduced = options.reduced ?? globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const thread = splash.querySelector(".splash-thread");
  const word = splash.querySelector(".splash-word");
  const art = splash.querySelector("svg");

  const ready = new Promise<void>((resolve) => {
    announce = resolve;
  });

  const held = globalThis.setTimeout(() => announce?.(), HOLD_AT_MOST);
  const slow = globalThis.setTimeout(() => splash.setAttribute("data-slow", ""), SLOW_AFTER);

  // With less motion the page already shows the end frame.
  const played = reduced || thread === null || word === null
    ? []
    : [
        animate(thread, { strokeDashoffset: [-1, 0] }, { duration: THREAD, ease: EASE_STANDARD }),
        animate(word, { opacity: [0, 1], transform: ["translateY(6px)", "translateY(0px)"] }, { duration: FAST, delay: THREAD, ease: EASE_STANDARD }),
      ];

  await Promise.all([ready, ...played]);
  globalThis.clearTimeout(held);
  globalThis.clearTimeout(slow);
  announce = null;

  const header = root.querySelector("[data-page-header]");

  await (reduced || art === null
    ? animate(splash, { opacity: [1, 0] }, { duration: FAST })
    : Promise.all([
        animate(art, { opacity: [1, 0], transform: ["translateY(0px)", "translateY(-8px)"] }, { duration: BASE, ease: EASE_EXIT }),
        animate(splash, { opacity: [1, 0] }, { duration: BASE, ease: EASE_EXIT }),
        ...(header === null ? [] : [animate(header, { transform: ["translateY(-100%)", "translateY(0px)"] }, { duration: SLOW, ease: EASE_STANDARD })]),
      ]));

  splash.remove();
}
